import Container from "../../../components/element/container";
import { useEffect, useMemo, useRef, useState } from "react";
import { MDBModalBody } from "mdb-react-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Col from "../../../components/element/col";
import Icon from "../../../components/element/icon";
import Image from "../../../components/element/image";
import Row from "../../../components/element/row";
import FullModal from "../../../components/modal/full.modal";
import { DcLogo } from "../../../assets/images";
import formatNumber from "../../../helpers/format.number";
import { handleCatchError } from "../../../helpers/handle.catch.error";
import { closeSavedCarsModal } from "../../../redux/reducer/modal.reducer";
import { getProfile } from "../../../redux/reducer/profile.reducer";
import AccountModalHeader from "../../../components/common/account.modal.header";
import { saveProduct } from "../../../redux/reducer/product.reducer";

const INITIAL_VISIBLE_CARS = 6;
const VISIBLE_CARS_INCREMENT = 6;
const EMPTY_SAVED_CARS = [];

export default function SavedCarModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const loadMoreRef = useRef(null);
  const [searchTerm] = useState("");
  const [visibleCarsCount, setVisibleCarsCount] =
    useState(INITIAL_VISIBLE_CARS);
  const { profileInfo, profileLoading } = useSelector((state) => state.profile);
  const { isOpenSavedCarsModal } = useSelector((state) => state.modal);
  const savedCars = useMemo(
    () =>
      Array.isArray(profileInfo?.saved_cars)
        ? profileInfo.saved_cars
        : EMPTY_SAVED_CARS,
    [profileInfo?.saved_cars],
  );
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredCars = useMemo(
    () =>
      savedCars.filter((car) => {
        if (!normalizedSearchTerm) return true;

        return [car?.name, car?.view_location, car?.category]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedSearchTerm));
      }),
    [normalizedSearchTerm, savedCars],
  );
  const visibleCars = filteredCars.slice(0, visibleCarsCount);
  const hasMoreCars = visibleCars.length < filteredCars.length;

  const handleClose = () => {
    dispatch(closeSavedCarsModal());
  };

  const handleOpenProduct = (car) => {
    if (!car?.id) return;

    // dispatch(getProductInfo({ id: car.id }));
    handleClose();
    navigate(
      `/${car.category === "car plate" ? "car-plate" : "car"}/${car.id}`,
      {
        state: { backgroundLocation: location },
      },
    );
  };

  const handleUnsaveProduct = (event, id) => {
    event.stopPropagation();

    dispatch(
      saveProduct({
        product_id: id,
        action: "remove",
      }),
    )
      .unwrap()
      .then(() => dispatch(getProfile()))
      .catch((error) => handleCatchError(error));
  };

  useEffect(() => {
    setVisibleCarsCount(INITIAL_VISIBLE_CARS);
  }, [normalizedSearchTerm, savedCars.length]);

  useEffect(() => {
    if (!isOpenSavedCarsModal || !hasMoreCars || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCarsCount((currentCount) =>
            Math.min(
              currentCount + VISIBLE_CARS_INCREMENT,
              filteredCars.length,
            ),
          );
        }
      },
      { rootMargin: "160px" },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [filteredCars.length, hasMoreCars, isOpenSavedCarsModal]);

  return (
    <FullModal
      independent
      show={isOpenSavedCarsModal}
      close={handleClose}
      showCloseButton
      centered
      scrollable
      screenSize="lg"
      className="product-action-modal saved-car-modal"
      dialogClassName="action-modal-dialog saved-car-modal-dialog"
      contentClassName="action-modal-content saved-car-modal-content"
      content={
        <MDBModalBody className="saved-car-modal-body">
          <AccountModalHeader
            title={t("Saved Cars")}
            subtitle={t("Track and revisit your favourite listings")}
            icon="akar-icons:heart"
          />

          <Col className="_col saved-car-list">
            {profileLoading ? (
              Array.from({ length: 4 }, (_, index) => (
                <span className="saved-car-skeleton" key={index} />
              ))
            ) : visibleCars.length > 0 ? (
              visibleCars.map((car) => (
                <article
                  className="_col saved-car-card"
                  key={car.id}
                  onClick={() => handleOpenProduct(car)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handleOpenProduct(car);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Container tag="div" className="saved-car-media">
                    <Image
                      src={car?.media?.[0]?.url || DcLogo}
                      alt={car?.name || t("Saved car")}
                      className={`_img saved-car-image ${
                        car?.media?.length > 0 ? "" : "default"
                      }`}
                    />

                    <span
                      className="saved-car-remove saved-car-favourite"
                      onClick={(event) => handleUnsaveProduct(event, car.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handleUnsaveProduct(event, car.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-label={`${t("Remove")} ${car?.name || t("car")}`}
                    >
                      <Icon
                        icon="solar:heart-bold"
                        className="_svg saved-car-like"
                      />
                    </span>
                  </Container>
                  <Col className="_col saved-car-card-copy">
                    <h3 className="_label saved-car-name">{car?.name}</h3>
                    <Row className="_row saved-car-meta">
                      <strong className="_label saved-car-price">
                        RM {formatNumber(car?.price)}
                      </strong>
                      {car?.properties?.mileage && (
                        <span className="_label saved-car-detail">
                          <Icon
                            icon="qlementine-icons:meter-high-16"
                            className="_svg saved-car-detail"
                          />
                          {formatNumber(car.properties.mileage)} km
                        </span>
                      )}
                      {car?.view_location && (
                        <span className="_label saved-car-location">
                          <Icon
                            icon="f7:location-fill"
                            className="_svg saved-car-location"
                          />
                          {car.view_location}
                        </span>
                      )}
                    </Row>
                    <Row className="_row saved-car-actions">
                      <span className="_label saved-car-view">
                        {t("View Details")}
                        <Icon
                          icon="solar:arrow-right-linear"
                          className="_svg saved-car-view"
                        />
                      </span>
                      <span
                        className="_label saved-car-remove-action"
                        onClick={(event) => handleUnsaveProduct(event, car.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            handleUnsaveProduct(event, car.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <Icon
                          icon="solar:trash-bin-trash-linear"
                          className="_svg saved-car-remove-action"
                        />
                        {t("Remove")}
                      </span>
                    </Row>
                  </Col>
                </article>
              ))
            ) : (
              <Col className="_col saved-car-empty">
                <Icon
                  icon="solar:heart-broken-linear"
                  className="_svg saved-car-empty"
                />
                <p className="_label saved-car-empty-title">
                  {searchTerm
                    ? t("No saved cars match your search")
                    : t("No Saved Car Found")}
                </p>
              </Col>
            )}
            {hasMoreCars && (
              <span className="saved-car-load-more" ref={loadMoreRef} />
            )}
          </Col>
        </MDBModalBody>
      }
    />
  );
}
